import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { getColorByString } from "@msuite/utils/colors";
import React, { memo } from "react";
import {
	CapacityCalendarCells,
	CapacityCalendarRowWrapper,
	useCapacityCalendarContext,
} from "../capacity-calendar";
import { CapacityPlannerBar, CapacityPlannerRowHead } from "../planner-view";
import { CapacityManagerProjectRow } from "./capacity-manager-project-row";
import { CapacityManagerSeparatorRow } from "./capacity-manager-separator-row";
import { useTicketCoverage } from "./use-ticket-coverage";
import type { CapacityManagerListItem } from "./types";

/** Props Interface */
interface CapacityManagerRowProps {
	item: CapacityManagerListItem;
}

function CapacityManagerRowImpl({ item }: CapacityManagerRowProps) {
	/** Context */
	const { getIsExpanded, toggleExpanded } = useCapacityCalendarContext();

	/** Hooks */
	const { t } = useTranslation();

	// Everything this manager carries, rolled into one reading of the window: the days they have
	// work on, and the days some of it has nobody on it. This is the row's whole point when it is
	// closed, which is how a manager row spends most of its life.
	const accentColor = getColorByString(item.fullName);
	const coverageGradient = useTicketCoverage(item.projects, accentColor);

	/** Computed */
	// A manager the window has no work for has no second level to open, so the handle is dead
	// rather than opening onto nothing. The row itself stays: who is free is an answer too.
	const canExpand = item.projects.length > 0;
	const isExpanded = canExpand && getIsExpanded(item.userId);
	const projectsLabel = item.projects.length
		? t("projects.active_projects_count", { count: String(item.projects.length) })
		: t("planning.crews.no_work_in_window");

	/** Render */
	return (
		<React.Fragment>
			<CapacityCalendarRowWrapper variant="content">
				<CapacityPlannerRowHead
					accentColor={accentColor}
					handle={
						<Button
							variant="white"
							size="xxs"
							icon={<Icon name={isExpanded ? "ChevronDown" : "ChevronRight"} />}
							isDisabled={!canExpand}
							onClick={() => toggleExpanded(item.userId)}
						/>
					}
				>
					<p className="font-semibold text-sm truncate">{item.fullName}</p>
					<p className="label truncate">{projectsLabel}</p>
				</CapacityPlannerRowHead>
				<CapacityCalendarCells />
				<CapacityPlannerBar
					gradient={coverageGradient}
					thickness="slim"
				/>
			</CapacityCalendarRowWrapper>
			{isExpanded &&
				item.projects.map((project) => (
					<CapacityManagerProjectRow
						key={project.projectId}
						item={project}
					/>
				))}
			{/* Only where there is a block to close. A row of closed managers is a plain list and the
			    line under each one already separates them; it is the projects and tickets an open row
			    unfolds that make the next manager hard to find. Rendered here rather than between rows
			    in the view, because those rows are this one's and it is what draws them. */}
			{isExpanded && <CapacityManagerSeparatorRow />}
		</React.Fragment>
	);
}

export const CapacityManagerRow = memo(CapacityManagerRowImpl);
